import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import '../styles/AdminBuyPlan.css';

const AdminBuyPlan = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [mobileNumber, setMobileNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [fetchingPlan, setFetchingPlan] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const planId = searchParams.get('planId');

  const normalizeMobileNumber = (value) => {
    const compact = String(value || '').trim().replace(/[\s()-]/g, '');

    if (compact.startsWith('+')) {
      const digits = compact.slice(1).replace(/\D/g, '');
      return `+${digits}`;
    }

    const digits = compact.replace(/\D/g, '');
    if (digits.length === 10) {
      return `+91${digits}`;
    }

    if (digits.length >= 11 && digits.length <= 15) {
      return `+${digits}`;
    }

    return digits;
  };

  useEffect(() => {
    const loadPlan = async () => {
      if (!planId) {
        setPlan(null);
        return;
      }

      try {
        setFetchingPlan(true);
        const response = await axios.get(`${API_BASE_URL}/api/plans/${planId}`);
        setPlan(response.data);
      } catch (err) {
        setPlan(null);
        setError(err.response?.data?.message || 'Unable to load selected plan details.');
      } finally {
        setFetchingPlan(false);
      }
    };

    loadPlan();
  }, [planId]);

  const handleGenerateOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const normalizedNumber = normalizeMobileNumber(mobileNumber);
    if (!/^\+[1-9][0-9]{9,14}$/.test(normalizedNumber)) {
      setError('Please enter a valid mobile number in international format (example: +919876543210).');
      return;
    }

    try {
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        API_ENDPOINTS.SUBSCRIPTIONS_GENERATE_OTP,
        {
          mobileNumber: normalizedNumber,
          planId: planId || null
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const smsDelivered = Boolean(response.data?.smsDelivered);
      const reason = response.data?.reason;
      const provider = response.data?.provider;
      const debugOtp = response.data?.debugOtp;

      if (!smsDelivered) {
        const details = [provider ? `Provider: ${provider}` : null, reason ? `Reason: ${reason}` : null]
          .filter(Boolean)
          .join(' | ');

        if (debugOtp) {
          setSuccess(`OTP generated. Debug OTP: ${debugOtp}`);
        } else {
          setSuccess('OTP generated for testing.');
        }

        setError(
          details
            ? `SMS was not delivered to your phone. ${details}`
            : 'SMS was not delivered to your phone. Please check provider setup.'
        );
      } else if (debugOtp) {
        setSuccess(`OTP generated successfully. Debug OTP: ${debugOtp}`);
      } else {
        setSuccess(response.data?.message || 'OTP sent successfully.');
      }
      setOtpSent(true);
      setOtpVerified(false);
      setMobileNumber(normalizedNumber);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!/^\d{6}$/.test(String(otpCode).trim())) {
      setError('Please enter a valid 6-digit OTP.');
      return;
    }

    try {
      setVerifying(true);
      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        API_ENDPOINTS.SUBSCRIPTIONS_VERIFY_OTP,
        {
          mobileNumber: mobileNumber.trim(),
          otp: String(otpCode).trim(),
          planId: planId || null
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setOtpVerified(true);
      setSuccess(response.data?.message || 'OTP verified successfully.');
      window.alert('OTP verified successfully. You can continue to payment gateway.');
    } catch (err) {
      setOtpVerified(false);
      setError(err.response?.data?.message || 'OTP verification failed.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="admin-buy-plan-page">
      <div className="admin-buy-plan-card">
        <h2>Buy Plan</h2>
        <p>Enter your mobile number and generate an OTP to continue plan purchase.</p>

        {fetchingPlan ? <div className="info">Loading selected plan...</div> : null}

        {plan ? (
          <div className="selected-plan-box">
            <h3>{plan.name}</h3>
            <p>Price: ${Number(plan.price || 0)}/month</p>
            <p>Users: {plan.users}</p>
            <p>Assets: {plan.assets}</p>
          </div>
        ) : (
          <div className="info">No plan selected. You can still generate OTP and continue later.</div>
        )}

        {error ? <div className="message error">{error}</div> : null}
        {success ? <div className="message success">{success}</div> : null}

        <form onSubmit={handleGenerateOtp}>
          <label htmlFor="mobileNumber">Mobile Number</label>
          <input
            id="mobileNumber"
            type="tel"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            placeholder="e.g. +919876543210"
            maxLength={16}
          />

          <div className="actions-row">
            <button type="submit" disabled={loading}>
              {loading ? 'Generating OTP...' : 'Generate OTP'}
            </button>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => navigate('/lab-admin/dashboard')}
            >
              Back to Dashboard
            </button>
          </div>
        </form>

        {otpSent ? (
          <form onSubmit={handleVerifyOtp} className="verify-form">
            <label htmlFor="otpCode">Enter OTP</label>
            <input
              id="otpCode"
              type="text"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="Enter 6-digit OTP"
              maxLength={6}
            />
            <div className="actions-row">
              <button type="submit" disabled={verifying}>
                {verifying ? 'Verifying OTP...' : 'Verify OTP'}
              </button>
            </div>
          </form>
        ) : null}

        {otpVerified ? (
          <div className="actions-row payment-row">
            <button 
              type="button" 
              onClick={() => navigate(`/payment-gateway?planId=${planId}&mobile=${encodeURIComponent(mobileNumber)}`)}
            >
              Proceed to Payment Gateway
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AdminBuyPlan;
